/**
 * 변환 결과를 인쇄 프리셋의 정확한 픽셀 크기로 맞춘다 (BOOTH-35).
 * 중앙 크롭(목표 비율) → 목표 크기 canvas 에 리사이즈 → JPEG 재인코딩.
 * Gemini 는 enum 비율(예: 3:2)로만 그리므로 엽서(1.48)처럼 살짝 다른 비율은 여기서 가장자리를 잘라 맞춘다.
 * 이 결과가 리뷰·S3 업로드·공유 페이지 모두에 쓰인다.
 * `overlay` 옵션으로 워터마크 이미지를 얹어 `branded` 사본과 `clean` 사본을 동시에 생성한다 (BOOTH-103).
 * 실패하면(디코딩 불가·canvas 한도 등) 원본을 그대로 돌려줘 손님 흐름을 막지 않는다.
 */
import { loadImage } from "./cropImage";
import { withJpegDpi } from "./jpegDensity";
import { DEFAULT_WATERMARK_STYLE, watermarkOverlayRect, type WatermarkStyle } from "@fiket/booth-schema";
import { trackError } from "../telemetry/activity";

export interface FitOptions {
  /** JPEG 품질 (0–1). 기본 0.92 */
  quality?: number;
  /** JFIF 밀도 태그에 기록할 dpi — 프린터 드라이버가 물리 크기를 읽을 수 있게. */
  dpi?: number;
  /**
   * 워터마크 오버레이 이미지 (BOOTH-103).
   * 있으면 크롭·리사이즈 후 하단에 얹어 `branded` 사본을 만든다.
   * 얹기 실패 시 warn 후 `branded === clean` 으로 폴백 — 손님 플로우를 막지 않는다.
   */
  overlay?: HTMLImageElement | null;
  /** 워터마크 위치·크기 (BOOTH-129). 없으면 기본(하단 띠) = BOOTH-103 동작. */
  overlayStyle?: WatermarkStyle;
}

/**
 * 크롭·리사이즈 후 워터마크 없는 사본(`clean`)과 워터마크 얹은 사본(`branded`)을 모두 반환한다.
 * `overlay` 없으면 두 값이 같은 문자열.
 * 바깥 catch(디코드·canvas 실패)는 두 값 모두 원본 dataUrl 로 폴백 — 기존 동작과 동일.
 */
export async function fitDataUrlToPixelsPair(
  dataUrl: string,
  target: { w: number; h: number },
  { quality = 0.92, dpi, overlay = null, overlayStyle = DEFAULT_WATERMARK_STYLE }: FitOptions = {}
): Promise<{ clean: string; branded: string }> {
  if (!Number.isFinite(target.w) || !Number.isFinite(target.h) || target.w <= 0 || target.h <= 0) {
    return { clean: dataUrl, branded: dataUrl };
  }
  try {
    const img = await loadImage(dataUrl);
    const srcW = img.naturalWidth;
    const srcH = img.naturalHeight;
    if (!srcW || !srcH) return { clean: dataUrl, branded: dataUrl };

    const targetRatio = target.w / target.h;
    const srcRatio = srcW / srcH;
    let cropW = srcW;
    let cropH = srcH;
    if (srcRatio > targetRatio) {
      cropW = Math.round(srcH * targetRatio);
    } else if (srcRatio < targetRatio) {
      cropH = Math.round(srcW / targetRatio);
    }
    const sx = Math.round((srcW - cropW) / 2);
    const sy = Math.round((srcH - cropH) / 2);

    const canvas = document.createElement("canvas");
    canvas.width = target.w;
    canvas.height = target.h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { clean: dataUrl, branded: dataUrl };
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, target.w, target.h);

    const outRaw = canvas.toDataURL("image/jpeg", quality);
    // canvas 한도 초과 등으로 인코딩이 실패하면 빈/기본 dataURL 이 나온다 — 원본 유지.
    if (!outRaw.startsWith("data:image/jpeg") || outRaw.length < 100) {
      return { clean: dataUrl, branded: dataUrl };
    }
    const clean = dpi ? withJpegDpi(outRaw, dpi) : outRaw;

    // 워터마크 오버레이 — 실패 시 branded = clean
    let branded = clean;
    if (overlay) {
      try {
        const r = watermarkOverlayRect(target.w, target.h, overlayStyle);
        ctx.drawImage(overlay, r.x, r.y, r.w, r.h);
        const overlaidRaw = canvas.toDataURL("image/jpeg", quality);
        if (overlaidRaw.startsWith("data:image/jpeg") && overlaidRaw.length >= 100) {
          branded = dpi ? withJpegDpi(overlaidRaw, dpi) : overlaidRaw;
        } else {
          console.warn("[fitImage] 워터마크 합성 결과가 유효하지 않음 — 워터마크 없이 진행");
        }
      } catch (err) {
        console.warn("[fitImage] 워터마크 합성 실패 — 워터마크 없이 진행", err);
        branded = clean;
      }
    }

    return { clean, branded };
  } catch (err) {
    console.warn("[fitImage] 결과 리사이즈 실패 — 원본 사용:", err);
    trackError("fit_image_failed", err);
    return { clean: dataUrl, branded: dataUrl };
  }
}

